import { Box, Field, Flex, Heading, HStack, Input, Stack, Text, Textarea } from '@chakra-ui/react';
import { useState } from 'react';

import {
  CurrencyInput,
  DateInput,
  DropdownInput,
  NumberInput,
  PercentInput,
} from '~/components/inputs';
import { ProjectPageTemplate } from '~/components/layout/page-utilities/ProjectPageTemplate';
import { type ColumnDefinition, ReusableTable } from '~/components/tables/ReusableTable';

// Mock project data (would be replaced with real data from API/context)
const mockProject = {
  name: 'Placeholder School Name',
  description: '',
  dueDate: '',
  fiscalYearEnd: '',
  paymentMonths: [],
  initialTaxableValue: '',
  startYearGrowthRate: '',
  fiveYearGrowthRate: '',
  twentyYearGrowthRate: '',
  startingDRFBalance: '',
  maximumDRFBalance: '',
  minimumDRFBalance: '',
  firstFiscalYearEnd: '',
  initialTaxableValueProposal: '',
  startingMillage: '',
  peakDuration: '',
  minimumMillage: '',
  minimumDuration: '',
  firstYearInterestRate: '',
  interestRateMax: '',
  interestRateInterval: '',
  paymentInfo: {
    months: ['May', 'November'],
    amounts: {
      May: '',
      November: '',
    },
  },
};

// Interface for project details
interface ProjectDetails {
  name: string;
  description: string;
  dueDate: string;
  fiscalYearEnd: string;
  paymentMonths: string[];
  initialTaxableValue: string;
  startYearGrowthRate: string;
  fiveYearGrowthRate: string;
  twentyYearGrowthRate: string;
  startingDRFBalance: string;
  maximumDRFBalance: string;
  minimumDRFBalance: string;
  firstFiscalYearEnd: string;
  initialTaxableValueProposal: string;
  startingMillage: string;
  peakDuration: string;
  minimumMillage: string;
  minimumDuration: string;
  firstYearInterestRate: string;
  interestRateMax: string;
  interestRateInterval: string;
  paymentInfo: {
    months: string[];
    amounts: Record<string, string>;
  };
}

const ProjectDetailsPage = () => {
  const [projectDetails, setProjectDetails] = useState<ProjectDetails>(mockProject);

  // Handler for input changes
  const handleInputChange = (field: string, value: string) => {
    setProjectDetails((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Get current page title
  const getPageTitle = () => {
    if (projectDetails.name) {
      return projectDetails.name;
    }
    return 'Project Details';
  };

  // Sample payment month options for dropdown
  const paymentMonthOptions = [
    { value: 'January', label: 'January' },
    { value: 'February', label: 'February' },
    { value: 'March', label: 'March' },
    { value: 'April', label: 'April' },
    { value: 'May', label: 'May' },
    { value: 'June', label: 'June' },
    { value: 'July', label: 'July' },
    { value: 'August', label: 'August' },
    { value: 'September', label: 'September' },
    { value: 'October', label: 'October' },
    { value: 'November', label: 'November' },
    { value: 'December', label: 'December' },
  ];

  // Define column structure for payment months table
  const paymentColumns: ColumnDefinition<{ month: string; percentage: string }>[] = [
    {
      key: 'month',
      header: 'Payment Month',
      accessor: 'month',
      disableSort: true,
    },
    {
      key: 'percentage',
      header: 'Percentage',
      accessor: (item) => (
        <PercentInput
          label=""
          value={item.percentage}
          onChange={(value) => {
            setProjectDetails((prev) => ({
              ...prev,
              paymentInfo: {
                ...prev.paymentInfo,
                amounts: {
                  ...prev.paymentInfo.amounts,
                  [item.month]: value,
                },
              },
            }));
          }}
        />
      ),
      disableSort: true,
      cellTextAlign: 'center',
    },
  ];

  // Create table data from payment months
  const paymentTableData = projectDetails.paymentMonths.map((month) => ({
    month,
    percentage: projectDetails.paymentInfo.amounts[month] || '',
  }));

  return (
    <ProjectPageTemplate title={getPageTitle()}>
      <Flex flexDirection="column" gap={6}>
        <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
          <Heading size="md" mb={6}>
            Project Overview
          </Heading>

          <Stack gap={6}>
            <HStack align="flex-start" gap={6}>
              <Field.Root>
                <Field.Label>Project Name</Field.Label>
                <Input
                  value={projectDetails.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Enter project name"
                />
              </Field.Root>

              <DateInput
                label="Project Due Date"
                value={projectDetails.dueDate}
                onChange={(value) => handleInputChange('dueDate', value)}
              />
            </HStack>

            <Field.Root>
              <Field.Label>Project Description</Field.Label>
              <Textarea
                placeholder="Project requirements, goals, etc."
                value={projectDetails.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={4}
              />
            </Field.Root>
          </Stack>
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
          <Heading size="md" mb={6}>
            Project Details
          </Heading>

          <Stack gap={8}>
            {/* Growth Rates */}
            {/* <TaxableValueInputs
              initialTaxableValue={projectDetails.initialTaxableValue}
              startYearGrowthRate={projectDetails.startYearGrowthRate}
              fiveYearGrowthRate={projectDetails.fiveYearGrowthRate}
              twentyYearGrowthRate={projectDetails.twentyYearGrowthRate}
              onChange={(values: TaxValueInputsForm) => {
                setProjectDetails((prev) => ({
                  ...prev,
                  initialTaxableValue: values.initialTaxableValue,
                  startYearGrowthRate: values.startYearGrowthRate,
                  fiveYearGrowthRate: values.fiveYearGrowthRate,
                  twentyYearGrowthRate: values.twentyYearGrowthRate
                }));
              }}
            /> */}

            {/* DRF Parameters */}
            <Box>
              <Text fontWeight="medium" mb={4}>
                DRF Parameters
              </Text>
              <HStack align="flex-start" gap={6}>
                <CurrencyInput
                  label="Starting DRF Balance"
                  value={projectDetails.startingDRFBalance}
                  onChange={(value) => handleInputChange('startingDRFBalance', value)}
                  min={0}
                />

                <CurrencyInput
                  label="Maximum DRF Balance"
                  value={projectDetails.maximumDRFBalance}
                  onChange={(value) => handleInputChange('maximumDRFBalance', value)}
                  min={0}
                />

                <CurrencyInput
                  label="Minimum DRF Balance"
                  value={projectDetails.minimumDRFBalance}
                  onChange={(value) => handleInputChange('minimumDRFBalance', value)}
                  min={0}
                />
              </HStack>
            </Box>
          </Stack>
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
          <Heading size="md" mb={6}>
            Proposal Details
          </Heading>

          <Stack gap={8}>
            {/* Fiscal Information */}
            <Box>
              <Text fontWeight="medium" mb={4}>
                Fiscal Information
              </Text>
              <HStack align="flex-start" gap={6}>
                <DateInput
                  label="First Fiscal Year End"
                  value={projectDetails.firstFiscalYearEnd}
                  onChange={(value) => handleInputChange('firstFiscalYearEnd', value)}
                />

                <CurrencyInput
                  label="Initial Taxable Value"
                  value={projectDetails.initialTaxableValueProposal}
                  onChange={(value) => handleInputChange('initialTaxableValueProposal', value)}
                  min={0}
                />
              </HStack>
            </Box>

            {/* Millage Parameters */}
            <Box>
              <Text fontWeight="medium" mb={4}>
                Millage Parameters
              </Text>
              <HStack align="flex-start" gap={6}>
                <NumberInput
                  label="Starting Millage"
                  value={projectDetails.startingMillage}
                  onChange={(value) => handleInputChange('startingMillage', value)}
                  min={0}
                  precision={2}
                  step={0.01}
                />

                <NumberInput
                  label="Peak Duration (Years)"
                  value={projectDetails.peakDuration}
                  onChange={(value) => handleInputChange('peakDuration', value)}
                  min={0}
                  precision={0}
                />

                <NumberInput
                  label="Minimum Millage"
                  value={projectDetails.minimumMillage}
                  onChange={(value) => handleInputChange('minimumMillage', value)}
                  min={0}
                  precision={2}
                  step={0.01}
                />

                <NumberInput
                  label="Minimum Duration (Years)"
                  value={projectDetails.minimumDuration}
                  onChange={(value) => handleInputChange('minimumDuration', value)}
                  min={0}
                  precision={0}
                />
              </HStack>
            </Box>

            {/* Interest Rates */}
            <Box>
              <Text fontWeight="medium" mb={4}>
                Interest Rates
              </Text>
              <HStack align="flex-start" gap={6}>
                <PercentInput
                  label="First Year Interest Rate"
                  value={projectDetails.firstYearInterestRate}
                  onChange={(value) => handleInputChange('firstYearInterestRate', value)}
                />

                <PercentInput
                  label="Interest Rate Max"
                  value={projectDetails.interestRateMax}
                  onChange={(value) => handleInputChange('interestRateMax', value)}
                />

                <NumberInput
                  label="Interest Rate Interval"
                  value={projectDetails.interestRateInterval}
                  onChange={(value) => handleInputChange('interestRateInterval', value)}
                  min={0}
                  precision={0}
                />
              </HStack>
            </Box>

            {/* Payment Information */}
            <Box>
              <Text fontWeight="medium" mb={4}>
                Payment Information
              </Text>

              <Box mb={4}>
                <DropdownInput
                  label="Payment Months"
                  value={projectDetails.paymentMonths}
                  onChange={(value) => {
                    // Handle array of values
                    if (Array.isArray(value)) {
                      setProjectDetails((prev) => {
                        // Create a new payments object with the selected months
                        const newPaymentAmounts = { ...prev.paymentInfo.amounts };

                        // Add any new months
                        value.forEach((month) => {
                          if (!newPaymentAmounts[month]) {
                            newPaymentAmounts[month] = '';
                          }
                        });

                        return {
                          ...prev,
                          paymentMonths: value,
                          paymentInfo: {
                            ...prev.paymentInfo,
                            months: value,
                            amounts: newPaymentAmounts,
                          },
                        };
                      });
                    }
                  }}
                  options={paymentMonthOptions}
                  placeholder="Select payment months"
                  multiple
                  width="50%"
                />
              </Box>

              {projectDetails.paymentMonths.length > 0 && (
                <Box width="50%">
                  <ReusableTable columns={paymentColumns} data={paymentTableData} />
                </Box>
              )}
            </Box>
          </Stack>
        </Box>
      </Flex>
    </ProjectPageTemplate>
  );
};

export default ProjectDetailsPage;
